import {
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Stack,
  Alert,
  Grid,
} from "@mui/material";
import { useCreateTransaction } from "../../api/queries";
import { useRegistry } from "../../context/RegistryContext";
import { CurrencySchema } from "../../api/types";
import type {
  Category,
  ConfigDto,
  ExpenseType,
  Group,
  Source,
  TransactionDefaultsDto,
} from "../../api/types";
import { HOME_CURRENCY } from "../../logic/money";

// Foreign-currency options come straight from the backend's `Currency` enum
// (via the generated `CurrencySchema`), minus the home currency — so adding a
// currency server-side surfaces here automatically after `just generate`.
const FOREIGN_CURRENCIES = CurrencySchema.options.filter(
  (c) => c !== HOME_CURRENCY,
);
import { useTransactionFormState } from "./useTransactionFormState";
import { DatePicker } from "../DatePicker";
import {
  CategoryPickerField,
  ExpenseTypePickerField,
  GroupPickerField,
  SourcePickerField,
} from "../EntityPickerField";

interface TransactionFormContentProps {
  config: ConfigDto;
  defaults: TransactionDefaultsDto;
  onSuccess: () => void;
}

export const TransactionFormContent = ({
  defaults,
  onSuccess,
}: TransactionFormContentProps) => {
  const createTransaction = useCreateTransaction();
  const { categories, invoices, withHidden } = useRegistry();

  const { state, actions, computed, handleSubmit } = useTransactionFormState({
    defaults,
    createTransaction,
    onSuccess,
  });

  // The form holds names; the pickers take ids. Resolve against the full
  // registry: a configured default (Ukjent, say) may be hidden and is still
  // the value, so it must not fall through to the picker's placeholder.
  //
  // The other way, the single pickers hand back the picked entity and the form
  // reads its name from that. Looking the id up in this render's registry
  // found nothing for an entity just created inline, so the new value was
  // dropped instead of selected.
  const sourceId =
    withHidden.sources.find((s) => s.name === state.source)?.id ?? null;
  const groupId =
    withHidden.groups.find((g) => g.name === state.group)?.id ?? null;
  const expenseTypeId =
    withHidden.expenseTypes.find((e) => e.name === state.expenseType)?.id ??
    null;
  const categoryIds = state.selectedCategories
    .map((name) => withHidden.categories.find((c) => c.name === name)?.id)
    .filter((id): id is NonNullable<typeof id> => id !== undefined);
  const allInvoices = invoices || [];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Add Transaction
      </Typography>
      <Stack spacing={2}>
        {/* First line: Date, Source, Expense Type, Invoice, Group, Categories. */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DatePicker
              mode="single"
              label="Date Paid"
              required
              fullWidth
              value={state.datePaid || null}
              onChange={(v) => actions.setDatePaid(v ?? "")}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SourcePickerField
              required
              value={sourceId}
              onChange={(_, source) =>
                actions.setSource((source?.name ?? "") as Source)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ExpenseTypePickerField
              required
              value={expenseTypeId}
              onChange={(_, expenseType) =>
                actions.setExpenseType((expenseType?.name ?? "") as ExpenseType)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="invoice-label">Invoice (Optional)</InputLabel>
              <Select
                labelId="invoice-label"
                value={state.invoice || ""}
                label="Invoice (Optional)"
                onChange={(e) => actions.setInvoice(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {allInvoices.map((inv) => (
                  <MenuItem key={inv.id} value={inv.id}>
                    {inv.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <GroupPickerField
              required
              value={groupId}
              onChange={(_, group) =>
                actions.setGroup((group?.name ?? "") as Group)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CategoryPickerField
              multiple
              label="Categories"
              value={categoryIds}
              onChange={(ids) =>
                actions.setSelectedCategories(
                  ids
                    .map((id) => categories.find((c) => c.id === id)?.name)
                    .filter((n): n is Category => typeof n === "string"),
                )
              }
            />
          </Grid>
        </Grid>

        {/* Second line: Merchant and Description */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Merchant"
              value={state.merchant}
              onChange={(e) => actions.setMerchant(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Description"
              value={state.description}
              onChange={(e) => actions.setDescription(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Third line: Currency + Original Amount, and Amount */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <FormControl fullWidth>
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Select
                    labelId="currency-label"
                    value={state.currency}
                    label="Currency"
                    onChange={(e) => actions.setCurrency(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {FOREIGN_CURRENCIES.map((code) => (
                      <MenuItem key={code} value={code}>
                        {code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={8}>
                <TextField
                  label="Foreign Amount"
                  type="number"
                  value={state.originalAmount}
                  onChange={(e) => actions.setOriginalAmount(e.target.value)}
                  disabled={computed.isForeignAmountDisabled}
                  fullWidth
                  slotProps={{ htmlInput: { step: "0.01" } }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Amount"
              type="number"
              value={state.amount}
              onChange={(e) => actions.setAmount(e.target.value)}
              required
              fullWidth
              slotProps={{ htmlInput: { step: "0.01" } }}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          disabled={createTransaction.isPending}
          fullWidth
        >
          {createTransaction.isPending ? "Adding..." : "Add Transaction"}
        </Button>

        {createTransaction.isError && (
          <Alert severity="error">{createTransaction.error.message}</Alert>
        )}
      </Stack>
    </Box>
  );
};
